import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { endoReportService } from '../../services/endoReportService';
import { practitionerService } from '../../services/practitionerService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import toast from 'react-hot-toast';
import { generateReferralPDF } from '../../utils/pdfGenerator';

const ReportManagementDashboard = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [practitioner, setPractitioner] = useState(null);
  const [selectedReports, setSelectedReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    reportType: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [sortConfig, setSortConfig] = useState({
    field: 'createdAt',
    direction: 'desc'
  });

  useEffect(() => {
    if (userProfile?.practitionerId) {
      loadData();
    }
  }, [userProfile?.practitionerId]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [reports, filters, searchTerm, sortConfig]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [reportsData, practitionerData] = await Promise.all([
        endoReportService?.getAll(userProfile?.practitionerId, filters),
        practitionerService?.getById(userProfile?.practitionerId)
      ]);
      setReports(reportsData || []);
      setPractitioner(practitionerData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast?.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...reports];

    if (filters?.status !== 'all') {
      filtered = filtered?.filter(r => r?.status === filters?.status);
    }

    if (filters?.reportType !== 'all') {
      filtered = filtered?.filter(r => r?.reportType === filters?.reportType);
    }

    if (filters?.dateFrom) {
      filtered = filtered?.filter(r => new Date(r?.createdAt) >= new Date(filters?.dateFrom));
    }

    if (filters?.dateTo) {
      filtered = filtered?.filter(r => new Date(r?.createdAt) <= new Date(filters?.dateTo));
    }

    if (searchTerm) {
      const term = searchTerm?.toLowerCase();
      filtered = filtered?.filter(r =>
        r?.patientFirstName?.toLowerCase()?.includes(term) ||
        r?.patientLastName?.toLowerCase()?.includes(term) ||
        r?.referrerName?.toLowerCase()?.includes(term) ||
        r?.diagnosis?.toLowerCase()?.includes(term)
      );
    }

    filtered?.sort((a, b) => {
      const aValue = a?.[sortConfig?.field];
      const bValue = b?.[sortConfig?.field];
      if (aValue === bValue) return 0;
      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig?.direction === 'asc' ? comparison : -comparison;
    });

    setFilteredReports(filtered);
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev?.field === field && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectReport = (reportId) => {
    setSelectedReports(prev =>
      prev?.includes(reportId)
        ? prev?.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const handleSelectAll = () => {
    if (selectedReports?.length === filteredReports?.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(filteredReports?.map(r => r?.id));
    }
  };

  const handleBulkExportPDF = async () => {
    try {
      if (selectedReports?.length === 0) {
        toast?.error('Veuillez sélectionner au moins un compte rendu');
        return;
      }

      toast?.loading('Export en cours...');
      
      for (const reportId of selectedReports) {
        const report = reports?.find(r => r?.id === reportId);
        if (!report) continue;

        const pdfData = {
          patientFirstName: report?.patientFirstName,
          patientLastName: report?.patientLastName,
          patientDateOfBirth: report?.patientDateOfBirth,
          referringDentistName: report?.referringDentistName,
          referringDentistEmail: report?.referringDentistEmail,
          referringDentistPhone: report?.referringDentistPhone,
          toothNumbers: report?.toothNumbers || [],
          clinicalNotes: report?.content,
          reportType: report?.reportType,
          reportTitle: report?.title,
          submissionDate: report?.createdAt
        };

        const doc = await generateReferralPDF(pdfData, practitioner);
        const blob = doc?.output('blob');
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `compte-rendu-${report?.patientLastName}-${report?.patientFirstName}.pdf`;
        document.body?.appendChild(link);
        link?.click();
        document.body?.removeChild(link);
        URL.revokeObjectURL(url);
      }

      toast?.dismiss();
      toast?.success(`${selectedReports?.length} PDF(s) exporté(s)`);
      setSelectedReports([]);
    } catch (error) {
      toast?.dismiss();
      console.error('Error exporting PDFs:', error);
      toast?.error('Erreur lors de l\'export des PDF');
    }
  };

  const handleBulkArchive = async () => {
    try {
      if (selectedReports?.length === 0) {
        toast?.error('Veuillez sélectionner au moins un compte rendu');
        return;
      }

      if (!confirm(`Archiver ${selectedReports?.length} compte(s) rendu(s) ?`)) {
        return;
      }

      for (const reportId of selectedReports) {
        await endoReportService?.update(reportId, { status: 'archived' });
      }

      toast?.success(`${selectedReports?.length} compte(s) rendu(s) archivé(s)`);
      setSelectedReports([]);
      await loadData();
    } catch (error) {
      console.error('Error archiving reports:', error);
      toast?.error('Erreur lors de l\'archivage');
    }
  };

  const handleDeleteReport = async (reportId) => {
    try {
      if (!confirm('Êtes-vous sûr de vouloir supprimer ce compte rendu ?')) {
        return;
      }

      await endoReportService?.delete(reportId);
      toast?.success('Compte rendu supprimé');
      await loadData();
    } catch (error) {
      console.error('Error deleting report:', error);
      toast?.error('Erreur lors de la suppression');
    }
  };

  const getReportTypeLabel = (type) => {
    const labels = {
      consultation: 'Consultation',
      cbct: 'CBCT',
      rte_treatment: 'Traitement Endo',
      surgery: 'Chirurgie',
      custom: 'Personnalisé'
    };
    return labels?.[type] || type;
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Brouillon' },
      final: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Final' },
      sent: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Envoyé' },
      archived: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Archivé' }
    };
    const badge = badges?.[status] || badges?.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge?.bg} ${badge?.text}`}>
        {badge?.label}
      </span>
    );
  };

  const statistics = {
    total: reports?.length || 0,
    draft: reports?.filter(r => r?.status === 'draft')?.length || 0,
    final: reports?.filter(r => r?.status === 'final')?.length || 0,
    sent: reports?.filter(r => r?.status === 'sent')?.length || 0,
    avgCompletionTime: '45s' // Mock data
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] via-[#1A1A1E] to-[#0A0A0F] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300 text-lg">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gestion des Comptes Rendus | EndoReferral</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] via-[#1A1A1E] to-[#0A0A0F] py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/practitioner-dashboard')}
              className="mb-4"
            >
              <Icon name="ArrowLeft" size={20} className="mr-2" />
              Retour au tableau de bord
            </Button>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Gestion des Comptes Rendus</h1>
                <p className="text-gray-400">Gérez tous vos comptes rendus endodontiques générés par IA</p>
              </div>
              <Button
                onClick={() => navigate('/ai-report-generator')}
                className="flex items-center gap-2"
              >
                <Icon name="Plus" size={20} />
                Nouveau compte rendu
              </Button>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total</p>
                  <p className="text-2xl font-bold text-white">{statistics?.total}</p>
                </div>
                <Icon name="FileText" size={24} className="text-blue-400" />
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Brouillons</p>
                  <p className="text-2xl font-bold text-white">{statistics?.draft}</p>
                </div>
                <Icon name="Edit" size={24} className="text-yellow-400" />
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Finalisés</p>
                  <p className="text-2xl font-bold text-white">{statistics?.final}</p>
                </div>
                <Icon name="CheckCircle" size={24} className="text-green-400" />
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Envoyés</p>
                  <p className="text-2xl font-bold text-white">{statistics?.sent}</p>
                </div>
                <Icon name="Send" size={24} className="text-blue-400" />
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Temps moyen</p>
                  <p className="text-2xl font-bold text-white">{statistics?.avgCompletionTime}</p>
                </div>
                <Icon name="Clock" size={24} className="text-purple-400" />
              </div>
            </div>
          </div>

          {/* Filters & Search */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Input
                placeholder="Rechercher patient, confrère..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="md:col-span-2"
              />
              <Select
                value={filters?.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e?.target?.value }))}
              >
                <option value="all">Tous les statuts</option>
                <option value="draft">Brouillon</option>
                <option value="final">Final</option>
                <option value="sent">Envoyé</option>
                <option value="archived">Archivé</option>
              </Select>
              <Select
                value={filters?.reportType}
                onChange={(e) => setFilters(prev => ({ ...prev, reportType: e?.target?.value }))}
              >
                <option value="all">Tous les types</option>
                <option value="consultation">Consultation</option>
                <option value="cbct">CBCT</option>
                <option value="rte_treatment">Traitement Endo</option>
                <option value="surgery">Chirurgie</option>
                <option value="custom">Personnalisé</option>
              </Select>
              <Button
                variant="outline"
                onClick={() => {
                  setFilters({ status: 'all', reportType: 'all', dateFrom: '', dateTo: '' });
                  setSearchTerm('');
                }}
              >
                Réinitialiser
              </Button>
            </div>
          </div>

          {/* Batch Actions */}
          {selectedReports?.length > 0 && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-4 mb-6 flex items-center justify-between">
              <p className="text-white">
                {selectedReports?.length} compte(s) rendu(s) sélectionné(s)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleBulkExportPDF}
                >
                  <Icon name="Download" size={16} className="mr-2" />
                  Exporter PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={handleBulkArchive}
                >
                  <Icon name="Archive" size={16} className="mr-2" />
                  Archiver
                </Button>
              </div>
            </div>
          )}

          {/* Reports Table */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedReports?.length === filteredReports?.length && filteredReports?.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th
                      className="px-4 py-3 text-left text-sm font-medium text-gray-300 cursor-pointer hover:text-white"
                      onClick={() => handleSort('patientLastName')}
                    >
                      Patient
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Statut</th>
                    <th
                      className="px-4 py-3 text-left text-sm font-medium text-gray-300 cursor-pointer hover:text-white"
                      onClick={() => handleSort('createdAt')}
                    >
                      Date création
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Confrère</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Version</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredReports?.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-12 text-center text-gray-400">
                        <Icon name="FileText" size={48} className="mx-auto mb-4 text-gray-500" />
                        <p>Aucun compte rendu trouvé</p>
                      </td>
                    </tr>
                  ) : (
                    filteredReports?.map(report => (
                      <tr key={report?.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedReports?.includes(report?.id)}
                            onChange={() => handleSelectReport(report?.id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-white font-medium">
                              {report?.patientLastName} {report?.patientFirstName}
                            </p>
                            <p className="text-xs text-gray-400">
                              {report?.patientYearOfBirth ? `Né(e) en ${report?.patientYearOfBirth}` : ''}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-300">
                            {getReportTypeLabel(report?.reportType)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(report?.status)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-300">
                            {new Date(report?.createdAt)?.toLocaleDateString('fr-FR')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-300">
                            {report?.referrerName || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-300">v{report?.version}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/report-editor-preview?reportId=${report?.id}`)}
                              title="Éditer"
                            >
                              <Icon name="Edit" size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteReport(report?.id)}
                              title="Supprimer"
                            >
                              <Icon name="Trash2" size={16} className="text-red-400" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportManagementDashboard;